import {
  beautify,
  createActivateAction,
  createDeactivateAction,
  createViewEdit,
  formatDate,
  itemExists,
  returnObject,
  useGetUserPermissions,
} from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { URL_USERS } from "../../utils/urls";
import { CAN_ADD_LOAN_USER, CAN_EDIT_USERS } from "../../utils/constants";
import { StatusTags } from "../../components/common/registerUtils";
import useSet from "../common/useSet";
import { useEffect } from "react";
import { Button, message } from "antd";
import { Link, useParams } from "react-router-dom";
import { useGlobalContext } from "../../context/GlobalContext";
import { API_USER_ACCOUNT } from "../../utils/apis";

export const useUsers = () => {
  const { success, error } = useSet();
  const permissions = useGetUserPermissions();
  const { updateData } = useGlobalContext();
  const { id } = useParams<{ id?: string }>();

  const tableColumns = () => [
    {
      id: "user_number",
      header: "User Number",
      filter: {
        type: "text",
      },
    },
    {
      id: "username",
      header: "Username",
      filter: {
        type: "text",
      },
    },
    {
      id: "first_name",
      header: "First Name",
      filter: {
        type: "text",
      },
    },
    {
      id: "last_name",
      header: "Last Name",
      filter: {
        type: "text",
      },
    },
    {
      id: "email",
      header: "Email",
      filter: {
        type: "text",
      },
    },
    {
      id: "phone_number",
      header: "Phone Number",
      filter: {
        type: "text",
      },
    },
    {
      id: "role",
      header: "Role",
      filter: {
        type: "text",
        key: "role_name",
      },
    },
    {
      id: "user_status",
      header: "Status",
      filter: {
        type: "select",
        options: [
          { id: "true", name: "True" },
          { id: "false", name: "False" },
        ],
      },
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];

  useEffect(() => {
    if (success) {
      message.success(`Updated user status`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }

    if (Object?.keys(error)?.length > 0) {
      message.error(error?.message || "Failed to update status");
    }
  }, [success, error]);

  const handleViewAttachment = (url: string) => {
    if (url) {
      updateData({ openAttachment: true, selectedAttachment: url });
    } else {
      console.warn("No attachment URL provided");
    }
  };

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      const row = {
        ...item,
        user_number: (
          <Link className="link-with-icon" to={`/users/view/${item?.id}`}>
            {item?.user_number}
          </Link>
        ),
        date_joined: formatDate(item.date_joined),
        role: beautify(item?.role),
        // full_name: `${item?.first_name || ""} ${item?.last_name || ""} ${item?.other_name || ""}`,
        user_status: <StatusTags status={item?.user_status} />,

        actions: (
          <FormActions
            actions={[
              ...createViewEdit(
                URL_USERS,
                item?.id,
                itemExists(permissions, [CAN_ADD_LOAN_USER, CAN_EDIT_USERS]),
              ),
              ...(item?.user_status === "deactivated"
                ? [
                    {
                      label: "Reactivate",
                      deleteUrl: `accounts/users/${item?.id}/reactivate/`,
                      applicationId: item?.id,
                    },
                  ]
                : item?.user_status === "blocked" ||
                    item?.user_status === "suspended"
                  ? [
                      {
                        label: "Reactivate",
                        deleteUrl: `accounts/users/${item?.id}/reactivate/`,
                        applicationId: item?.id,
                      },
                    ]
                  : item?.user_status === "pending"
                    ? [
                        createActivateAction(
                          `accounts/users/${item?.id}/activate/`,
                          item?.first_name,
                        ),
                      ]
                    : [
                        createDeactivateAction(
                          `accounts/users/${item?.id}/deactivate/`,
                          item?.first_name,
                          {
                            is_active: false,
                          },
                          {
                            showReasonField: true,
                            method: "POST",
                          },
                        ),
                        {
                          label: "Suspend",
                          deleteUrl: `accounts/users/${item?.id}/suspend/`,
                          applicationId: item?.id,
                        },
                        {
                          label: "Block",
                          deleteUrl: `accounts/users/${item?.id}/block/`,
                          applicationId: item?.id,
                        },
                      ]),
            ]}
          />
        ),
      };
      return row;
    }),
  ];

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "User Details",
          fields: {
            username: item?.username,
            first_name: item?.first_name,
            last_name: item?.last_name,
            other_name: item?.other_name,
            email: item?.email,
            phone_number: item?.phone_number,
            is_active: <StatusTags status={item?.user_status} />,
            ...returnObject(item?.user_status === "deactivated", {
              deactivation_reason: item?.deactivation_reason,
            }),
            date_joined: formatDate(item?.date_joined),
          },
        },
        {
          title: "Profile Details",
          fields: {
            nationality: item?.profile?.type_of_nationality,
            department: item?.profile?.department_name,
            NIN: item?.profile?.nin,
            TIN: item?.profile?.tin,
          },
        },
        ...(item?.attachments && item?.attachments?.length > 0
          ? [
              {
                title: "Documents",
                fields: {
                  attachments: (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {item?.attachments.map((att: any, index: number) => (
                        <Button
                          key={index}
                          type="primary"
                          style={{
                            marginBottom: 4,
                            width: "fit-content",
                            paddingLeft: 10,
                            paddingRight: 10,
                          }}
                          size="large"
                          onClick={() => handleViewAttachment(att.file)}
                        >
                          Click To View {att.caption} attachment
                        </Button>
                      ))}
                    </div>
                  ),
                },
              },
            ]
          : []),
      ],
    };
  };

  const endpoint = `${API_USER_ACCOUNT}${id}/logs/`;

  return { tableColumns, tableData, viewData, endpoint };
};
